// src/components/tutor/mobile/MobileTutorSchedule.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { IoMdNotificationsOutline } from "react-icons/io";
import { FaCheckCircle } from "react-icons/fa";
import { FaTriangleExclamation } from "react-icons/fa6";
import { LuX } from "react-icons/lu";

import { useTutorScheduleModel } from "../hooks/useTutorScheduleModel";
import { addDays, startOfWeekMonday } from "../../admin/scheduling/dateUtils";
import { fetchStudentsByIds } from "../../admin/scheduling/schedulingApi";
import { fetchSessionLogsBySessionIds } from "../session_logs/sessionLogApi";
import {
  buildSessionNotificationsBySessionId,
  fetchDiagnosticNotificationsByStudentId,
  SCHEDULE_NOTIFICATION_WINDOWS,
} from "../../admin/scheduling/notifications/sessionNotifications";
import "./MobileSchedule.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getWeekNumber(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day  = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
}

function fmtMonthDay(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtWeekRange(start, end) {
  const sYear = start.getFullYear();
  const eYear = end.getFullYear();
  const sMonth = start.toLocaleDateString("en-US", { month: "short" });
  const eMonth = end.toLocaleDateString("en-US", { month: "short" });
  if (sYear !== eYear) return `${fmtMonthDay(start)}, ${sYear} – ${fmtMonthDay(end)}, ${eYear}`;
  if (sMonth !== eMonth) return `${fmtMonthDay(start)} – ${fmtMonthDay(end)}, ${sYear}`;
  return `${sMonth} ${start.getDate()} – ${end.getDate()}, ${sYear}`;
}

function fmtTime(ts) {
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function fmtFullDate(d) {
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

function statusBgClass(status) {
  const s = (status || "").toLowerCase().trim();
  if (s === "confirmed") return "msch-card--confirmed";
  if (s === "present" || s === "finished") return "msch-card--present";
  if (s === "cancelled" || s === "canceled") return "msch-card--cancelled";
  if (s === "pending") return "msch-card--pending";
  return "";
}

function extractDaySessions(dayData) {
  if (!dayData?.tutorRows?.length) return [];
  return dayData.tutorRows
    .flatMap((row) =>
      Object.values(row.blocks || {}).flatMap((block) =>
        (block.sessionLists || []).flat()
      )
    )
    .sort((a, b) => {
      const ta = a.start?.toDate ? a.start.toDate() : new Date(a.start);
      const tb = b.start?.toDate ? b.start.toDate() : new Date(b.start);
      return ta - tb;
    });
}

// ─── Note / notification bottom sheet ────────────────────────────────────────

function NoteSheet({ session, notifications, onClose }) {
  const hasNote          = !!session?.note?.trim();
  const conferenceNotifs = (notifications || []).filter((n) => n.type === "conference" && (n.noteHtml || n.label));
  const otherNotifs      = (notifications || []).filter((n) => n.type !== "conference");
  const timeStr          = `${fmtTime(session.start)} – ${fmtTime(session.end)}`;

  return (
    <>
      <div className="msch-overlay" onClick={onClose} />
      <div className="msch-sheet">
        <div className="msch-sheet-handle" />
        <div className="msch-sheet-header">
          <div className="msch-sheet-meta">
            <h2 className="msch-sheet-title">
              {session.studentName} ({session.studentGrade})
            </h2>
            <p className="msch-sheet-sub">{session.subject} · {timeStr}</p>
          </div>
          <button className="msch-sheet-close" onClick={onClose} aria-label="Close">
            <LuX size={17} />
          </button>
        </div>

        {hasNote && (
          <div className="msch-note-block">
            <div className="msch-note-label">Session Note</div>
            <div className="msch-note-body">{session.note}</div>
          </div>
        )}

        {conferenceNotifs.map((n, i) => (
          <div key={i} className="msch-note-block">
            <div className="msch-note-label">Conference</div>
            {n.noteHtml ? (
              <div
                className="msch-note-html"
                // Content is from our own backend — safe to render
                dangerouslySetInnerHTML={{ __html: n.noteHtml }}
              />
            ) : (
              <div className="msch-note-body">{n.label}</div>
            )}
          </div>
        ))}

        {otherNotifs.length > 0 && (
          <div className="msch-note-block">
            <div className="msch-note-label">Notices</div>
            {otherNotifs.map((n, i) => (
              <div key={i} className="msch-notice-row">
                <IoMdNotificationsOutline size={13} className="msch-notice-icon" />
                <span className="msch-notice-text">{n.label || n.type}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Session card ─────────────────────────────────────────────────────────────
// Icon positioning matches desktop SessionCard exactly:
//   Bell  → position: absolute; top: 8px; right: 8px
//   Check → position: absolute; bottom: 8px; right: 8px

function MobileSessionCard({ session, notifications, hasLog, isPast }) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const hasNote    = !!session.note?.trim();
  const notifCount = notifications?.length || 0;
  const isTappable = hasNote || notifCount > 0;

  // Desktop rule: left accent border only when session has a note
  const showAccent  = hasNote;
  const bgClass     = statusBgClass(session.status);
  const timeStr     = `${fmtTime(session.start)} – ${fmtTime(session.end)}`;

  const openSheet  = (e) => { e?.stopPropagation(); setSheetOpen(true); };
  const closeSheet = () => setSheetOpen(false);

  const showLogCheck   = isPast && hasLog;
  const showLogMissing = isPast && !hasLog;

  return (
    <>
      <div
        className={[
          "msch-card",
          bgClass,
          showAccent  ? "msch-card--note"     : "",
          isTappable  ? "msch-card--tappable"  : "",
        ].filter(Boolean).join(" ")}
        onClick={isTappable ? openSheet : undefined}
      >
        {/* Bell — top-right absolute, identical to desktop */}
        {notifCount > 0 && (
          <button
            className="msch-icon msch-icon--bell"
            onClick={openSheet}
            aria-label={`${notifCount} notice${notifCount !== 1 ? "s" : ""}`}
          >
            <IoMdNotificationsOutline size={13} />
            <span className="msch-icon-badge">{notifCount > 9 ? "9+" : notifCount}</span>
          </button>
        )}

        {/* Log check — bottom-right absolute, identical to desktop */}
        {showLogCheck && (
          <span className="msch-icon msch-icon--log" title="Session log submitted">
            <FaCheckCircle size={11} />
          </span>
        )}
        {showLogMissing && (
          <span className="msch-icon msch-icon--log-missing" title="Session log not yet submitted">
            <FaTriangleExclamation size={11} />
          </span>
        )}

        {/* Card body — right padding reserves space for absolute icons */}
        <div className="msch-card-name">
          {session.studentName} ({session.studentGrade})
        </div>
        <div className="msch-card-subject">{session.subject || "—"}</div>
        <div className="msch-card-time">{timeStr}</div>
      </div>

      {sheetOpen && (
        <NoteSheet
          session={session}
          notifications={notifications}
          onClose={closeSheet}
        />
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MobileTutorSchedule({ currentUserUid, targetDate }) {
  const { today, weekStart, setWeekStart, weekEnd, weekDays, model } =
    useTutorScheduleModel(currentUserUid);

  const now = useMemo(() => new Date(), []);

  // Deep-link: jump to the week containing `targetDate` when the prop changes.
  useEffect(() => {
    if (!targetDate) return;
    const d = new Date(targetDate);
    if (isNaN(d.getTime())) return;
    setWeekStart(startOfWeekMonday(d));
  }, [targetDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Supplementary data ──
  const [studentsById,         setStudentsById]         = useState({});
  const [diagNotifsByStudentId, setDiagNotifsByStudentId] = useState({});
  const [sessionLogBySessionId, setSessionLogBySessionId] = useState({});

  const visibleSessions = useMemo(() => {
    const seen = new Map();
    Object.values(model || {}).forEach((dayData) => {
      extractDaySessions(dayData).forEach((s) => {
        if (s?.id && !seen.has(s.id)) seen.set(s.id, s);
      });
    });
    return [...seen.values()];
  }, [model]);

  const visibleSessionIds = useMemo(
    () => visibleSessions.map((s) => s.id).filter(Boolean),
    [visibleSessions]
  );
  const visibleStudentIds = useMemo(
    () => [...new Set(visibleSessions.map((s) => s.studentId).filter(Boolean))],
    [visibleSessions]
  );

  useEffect(() => {
    let alive = true;
    if (!visibleStudentIds.length) { setStudentsById({}); return; }
    fetchStudentsByIds(visibleStudentIds)
      .then((students) => {
        if (!alive) return;
        const map = {};
        students.forEach((s) => { if (s?.id) map[s.id] = s; });
        setStudentsById(map);
      })
      .catch(() => { if (alive) setStudentsById({}); });
    return () => { alive = false; };
  }, [visibleStudentIds]);

  useEffect(() => {
    let alive = true;
    const students = Object.values(studentsById);
    if (!students.length) { setDiagNotifsByStudentId({}); return; }
    fetchDiagnosticNotificationsByStudentId(students, {
      activeDays: SCHEDULE_NOTIFICATION_WINDOWS.diagnosticDays,
      now: new Date(),
    })
      .then((map) => { if (alive) setDiagNotifsByStudentId(map || {}); })
      .catch(() => { if (alive) setDiagNotifsByStudentId({}); });
    return () => { alive = false; };
  }, [studentsById]);

  useEffect(() => {
    let alive = true;
    if (!visibleSessionIds.length) { setSessionLogBySessionId({}); return; }
    fetchSessionLogsBySessionIds(visibleSessionIds)
      .then((logs) => { if (alive) setSessionLogBySessionId(logs || {}); })
      .catch(() => { if (alive) setSessionLogBySessionId({}); });
    return () => { alive = false; };
  }, [visibleSessionIds]);

  const sessionNotificationsBySessionId = useMemo(
    () =>
      buildSessionNotificationsBySessionId({
        sessions: visibleSessions,
        studentsById,
        diagnosticNotificationsByStudentId: diagNotifsByStudentId,
        conferenceActiveDays: SCHEDULE_NOTIFICATION_WINDOWS.conferenceDays,
        now: new Date(),
      }),
    [visibleSessions, studentsById, diagNotifsByStudentId]
  );

  // ── Week metadata ──
  const weekNum          = getWeekNumber(weekStart);
  const weekRangeStr     = fmtWeekRange(weekStart, weekEnd);
  const currentWeekStart = useMemo(() => startOfWeekMonday(today), [today]);
  const isCurrentWeek    = weekStart.toDateString() === currentWeekStart.toDateString();
  const goToToday        = () => setWeekStart(currentWeekStart);
  const [showPastDays, setShowPastDays] = useState(false);

  const visibleDays = useMemo(
    () => weekDays.filter(({ dayKey }) => extractDaySessions(model[dayKey]).length > 0),
    [weekDays, model]
  );

  const todayStartMs = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime(),
    [today]
  );

  const { pastVisibleDays, upcomingVisibleDays } = useMemo(() => {
    if (!isCurrentWeek) {
      return { pastVisibleDays: [], upcomingVisibleDays: visibleDays };
    }
    const past = [];
    const upcoming = [];
    visibleDays.forEach((d) => {
      const dayMs = new Date(
        d.date.getFullYear(),
        d.date.getMonth(),
        d.date.getDate()
      ).getTime();
      if (dayMs < todayStartMs) past.push(d);
      else upcoming.push(d);
    });
    return { pastVisibleDays: past, upcomingVisibleDays: upcoming };
  }, [visibleDays, isCurrentWeek, todayStartMs]);

  const hasHiddenPastDays = isCurrentWeek && !showPastDays && pastVisibleDays.length > 0;
  const renderedDays = useMemo(() => {
    if (!isCurrentWeek) return visibleDays;
    if (showPastDays) return visibleDays;
    return upcomingVisibleDays;
  }, [isCurrentWeek, showPastDays, visibleDays, upcomingVisibleDays]);

  useEffect(() => {
    setShowPastDays(false);
  }, [weekStart]);

  // ── Refs ──
  const containerRef = useRef(null);
  const touchStartX  = useRef(null);
  const touchStartY  = useRef(null);
  const revealStartX = useRef(null);
  const revealStartY = useRef(null);

  const handleBodyTouchStart = (e) => {
    if (!hasHiddenPastDays) return;
    const el = containerRef.current;
    if (!el || el.scrollTop > 0) return;
    revealStartX.current = e.touches[0].clientX;
    revealStartY.current = e.touches[0].clientY;
  };

  const handleBodyTouchEnd = (e) => {
    if (!hasHiddenPastDays) return;
    if (revealStartY.current == null || revealStartX.current == null) return;
    const dy = e.changedTouches[0].clientY - revealStartY.current;
    const dx = Math.abs(e.changedTouches[0].clientX - revealStartX.current);
    if (dy > 42 && dx < 28) {
      setShowPastDays(true);
    }
    revealStartX.current = null;
    revealStartY.current = null;
  };

  // ── Horizontal swipe for week navigation ──
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (Math.abs(dx) > 60 && Math.abs(dx) > dy * 1.4) {
      setWeekStart(addDays(weekStart, dx < 0 ? 7 : -7));
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      className="msch-container"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Week header ── */}
      <div className="msch-week-header">
        <div className="msch-week-meta">
          <span className="msch-week-num">Week {weekNum}</span>
          <span className="msch-week-range">{weekRangeStr}</span>
        </div>
        {!isCurrentWeek && (
          <button className="msch-today-pill" onClick={goToToday}>Today</button>
        )}
      </div>

      {/* ── Schedule body ── */}
      <div
        className="msch-body"
        ref={containerRef}
        key={weekStart.getTime()}
        onTouchStart={handleBodyTouchStart}
        onTouchEnd={handleBodyTouchEnd}
      >
        {hasHiddenPastDays && (
          <button
            type="button"
            className="msch-show-earlier"
            onClick={() => setShowPastDays(true)}
          >
            Show earlier this week
          </button>
        )}

        {visibleDays.length === 0 ? (
          <div className="msch-empty">
            <p className="msch-empty-text">No sessions this week.</p>
            {!isCurrentWeek && (
              <button className="msch-empty-today" onClick={goToToday}>
                Go to current week
              </button>
            )}
          </div>
        ) : (isCurrentWeek && !showPastDays && upcomingVisibleDays.length === 0) ? (
          <div className="msch-empty">
            <p className="msch-empty-text">No upcoming sessions this week.</p>
          </div>
        ) : (
          renderedDays.map(({ dayKey, date }) => {
            const sessions = extractDaySessions(model[dayKey]);
            const isToday  = date.toDateString() === today.toDateString();
            const weekday  = isToday
              ? "Today"
              : date.toLocaleDateString("en-US", { weekday: "long" });

            return (
              <div
                key={dayKey}
                className="msch-day-group"
              >
                <div className={`msch-day-header${isToday ? " msch-day-header--today" : ""}`}>
                  <span className="msch-day-weekday">{weekday}</span>
                  <span className="msch-day-date">{fmtFullDate(date)}</span>
                </div>

                <div className="msch-day-cards">
                  {sessions.map((session) => {
                    const start  = session.start?.toDate ? session.start.toDate() : new Date(session.start);
                    const isPast = start < now;
                    return (
                      <MobileSessionCard
                        key={session.id}
                        session={session}
                        notifications={sessionNotificationsBySessionId[session.id] || []}
                        hasLog={!!sessionLogBySessionId[session.id]}
                        isPast={isPast}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })
        )}

        <div className="msch-swipe-hint" aria-hidden="true">
          Swipe left or right to change week
        </div>
      </div>
    </div>
  );
}
